namespace $.$$ {

	export class $shm_hitalama_map_book_tools_float extends $.$shm_hitalama_map_book_tools_float {

		position_key( axis: 'x' | 'y' ) {
			return `$shm_hitalama_map_book_tools_float.position.${ axis }`
		}

		vertical() {
			return this.$.$mol_window.size().width < 280
		}

		max_height() {
			return `${ Math.max( 40, this.$.$mol_window.size().height - 16 ) }px`
		}

		bounds() {
			const screen = this.$.$mol_window.size()
			const map_rect = this.Map_page().view_rect()
			const tools = this.Tools().view_rect()
			const width = tools?.width ?? ( this.vertical() ? 48 : 192 )
			const height = tools?.height ?? ( this.vertical() ? 160 : 40 )
			const margin = 8

			return {
				min_x: 0,
				max_x: Math.max( 0, Math.min( screen.width, map_rect?.right ?? screen.width ) - width ),
				min_y: margin,
				max_y: Math.max( margin, screen.height - height - margin ),
			}
		}

		x( next?: number ) {
			const bounds = this.bounds()
			const key = this.position_key( 'x' )
			const value = next === undefined
				? this.$.$mol_state_local.value< number >( key ) ?? bounds.max_x
				: this.$.$mol_state_local.value( key, next ) ?? next
			return Math.min( bounds.max_x, Math.max( bounds.min_x, value ) )
		}

		y( next?: number ) {
			const bounds = this.bounds()
			const key = this.position_key( 'y' )
			const value = next === undefined
				? this.$.$mol_state_local.value< number >( key ) ?? 72
				: this.$.$mol_state_local.value( key, next ) ?? next
			return Math.min( bounds.max_y, Math.max( bounds.min_y, value ) )
		}

		left() {
			return `${ this.x() }px`
		}

		top() {
			return `${ this.y() }px`
		}

		auto() {
			return [ ...super.auto(), this.pointer_listener() ]
		}

		@ $mol_mem
		pointer_listener() {
			return new $mol_dom_listener(
				this.$.$mol_dom_context.document,
				'pointermove',
				this.pointer_move_async,
			)
		}

		pointer_move_async = $mol_wire_async( ( event: PointerEvent ) => {
			this.pointer_pos( [ event.clientX, event.clientY ] )
			if( this.draggers_suppressed() && !this.pointer_near() ) {
				this.draggers_suppressed( false )
			}
		} )

		pointer_near() {
			const point = this.pointer_pos()
			if( point.length < 2 ) return ''

			const rect = this.Tools().view_rect()
			if( !rect ) return ''
			const y = rect.top + rect.height / 2
			const left = Math.hypot( point[0] - rect.left + 16, point[1] - y )
			const right = Math.hypot( point[0] - rect.right - 16, point[1] - y )
			if( Math.min( left, right ) > 60 ) return ''
			return left <= right ? 'left' : 'right'
		}

		dragger_side() {
			if( this.Drag().dragged() ) return this.drag_side()
			const side = this.draggers_suppressed() ? '' : this.pointer_near()
			if( !side ) return ''

			const bounds = this.bounds()
			if( side === 'left' && this.x() <= bounds.min_x ) return 'right'
			if( side === 'right' && this.x() >= bounds.max_x ) return 'left'
			return side
		}

		drag_left_start( event?: PointerEvent | null ) {
			return this.drag_start( 'left', event )
		}

		drag_right_start( event?: PointerEvent | null ) {
			return this.drag_start( 'right', event )
		}

		drag_start( side: string, event?: PointerEvent | null ) {
			if( !event ) return null
			event.preventDefault()
			event.stopPropagation()
			this.drag_side( side )
			this.draggers_suppressed( false )
			this.Drag().pointerdown( event )
			return event
		}

	}

	export class $shm_hitalama_map_book_tools_float_quick extends $.$shm_hitalama_map_book_tools_float_quick {
		private readonly tool_touches = new Map< string, PointerEvent >()
		private readonly tool_clicks = new Map< string, PointerEvent >()
		private readonly tool_buttons = new WeakSet< $mol_check >()

		Button( id: string ) {
			const button = super.Button( id )
			if( this.tool_buttons.has( button ) ) return button
			this.tool_buttons.add( button )
			const events = button.event.bind( button )
			const click = button.event_click.bind( button )
			button.event = ()=> ({
				... events(),
				pointerdown: event => this.tool_touch_start( id, event as PointerEvent ),
				pointerup: event => this.tool_touch_end( id, event as PointerEvent ),
				pointercancel: event => this.tool_touch_cancel( id, event as PointerEvent ),
			})
			button.event_click = event => {
				this.tool_click( id, event as MouseEvent )
				return click( event )
			}
			return button
		}

		tool_touch_start( id: string, event?: PointerEvent ) {
			this.tool_touches.delete( id )
			this.tool_clicks.delete( id )
			if( event?.pointerType === 'touch' ) this.tool_touches.set( id, event )
			return event ?? null
		}

		tool_touch_cancel( id: string, event?: PointerEvent ) {
			this.tool_touches.delete( id )
			return event ?? null
		}

		@ $mol_action
		tool_touch_end( id: string, event?: PointerEvent ) {
			const start = this.tool_touches.get( id )
			if( !event || !start || event.pointerId !== start.pointerId ) return event ?? null
			this.tool_touches.delete( id )
			if( this.gesture_moved( id ) || Math.hypot( event.clientX - start.clientX, event.clientY - start.clientY ) > 8 ) return event
			// Some tablet gestures omit click after drawing. Activate on release;
			// consume its optional compatibility click to avoid toggling twice.
			this.tool_clicks.set( id, event )
			this.checked( id, !this.checked( id ) )
			return event
		}

		tool_click( id: string, event?: MouseEvent ) {
			const touch = this.tool_clicks.get( id )
			this.tool_clicks.delete( id )
			if( touch && event && event.detail > 0 ) event.preventDefault()
			return event ?? null
		}

		@ $mol_mem
		ids( next?: string[] ) {
			const saved = this.$.$mol_state_local.value< string[] >( '$shm_hitalama_map_drawing_tools_order', next ) ?? []
			const available = this.available_ids()
			return [ ...new Set([ ...saved.filter( id => available.includes( id ) ), ...available ]) ]
		}

		item_title( id: string ) {
			switch( id ) {
				case 'select': return 'Выбор'
				case 'drawing': return 'Рисование'
				case 'rect': return 'Прямоугольник'
				case 'circle': return 'Круг'
				case 'text': return 'Текст'
				case 'text_md': return 'Markdown'
				default: return id
			}
		}

		item_hotkey( id: string ) {
			switch( id ) {
				case 'select': return 'S'
				case 'drawing': return 'P'
				case 'rect': return 'R'
				case 'circle': return 'C'
				case 'text': return 'T'
				case 'text_md': return 'M'
				default: return ''
			}
		}

		item_label( id: string ) {
			switch( id ) {
				case 'select': return this.Select_icon()
				case 'drawing': return this.Drawing_icon()
				case 'rect': return this.Rect_icon()
				case 'circle': return this.Circle_icon()
				case 'text': return this.Text_icon()
				case 'text_md': return this.Markdown_icon()
				default: return null
			}
		}

		checked( id: string, next?: boolean ): boolean {
			const mode = id === 'select' ? '' : id
			if( next !== undefined ) {
				this.tool_mode( next ? mode : '' )
				this.draggers_suppressed( true )
			}
			return this.tool_mode() === mode
		}

	}

}
